<template>
    <PageBody v-if="page.uuid" :page="page" :isMinimap="isMinimap" ref="pageBody" />
</template>

<script>
import { ref } from "vue";
import PageBody from "@/views/tools/page/PageBody.vue";
import {
    openGallery,
    resizeAllGridItems,
    searchQuery,
    setFontFamily,
    updateIframeHeight,
} from "@/shared/utils";
import { preparePage } from "@/shared/projects/prepareData";
import { PREVIEW_ROUTE_NAMES } from "@/views/preview/preview.routes";
import { projectsService } from "@/shared/projects/projects.service";
import { makeLogger } from "@/shared/consola";
import { PREVIEW_ACTIONS } from "@/views/tools/page/utils/usePreviewActions";
import { useScrollIntoView } from "@/shared/utils/useScrollIntoView";

const logger = makeLogger("PagesPreview");

export default {
    name: "PagesPreview",
    components: {
        PageBody,
    },
    setup() {
        const { scrollInto } = useScrollIntoView();
        return {
            scrollInto,
            waiting: ref(false),
            currentFont: ref("Inter"),
        };
    },
    data() {
        return {
            page: {},
            windowWidth: window.width,
            isMinimap: this.$route.query.minimap === "true",
        };
    },
    async mounted() {
        if (window.location === window.parent.location) {
            try {
                this.page = await this.loadPage(this.$route.params.projectUuid);
                await this.adjustIframe();
            } catch {
                this.$router.push({ name: PREVIEW_ROUTE_NAMES.notFound });
            }
        } else {
            window.addEventListener("message", this.messageFromParentWindow);
        }

        window.addEventListener("resize", this.resizeIframe);
    },
    beforeUnmount() {
        window.removeEventListener("message", this.messageFromParentWindow);
        window.removeEventListener("resize", this.resizeIframe);
    },
    methods: {
        async loadPage(uuid) {
            this.waiting = true;
            const page = await projectsService.getOne({ tool: "page", uuid });
            this.waiting = false;
            return preparePage(page);
        },
        // messages from parent window (e.g. from the editor) send via postMessage to iframe
        async messageFromParentWindow(event) {
            logger.debug("handleEventFormParent", event);
            const { action } = event.data;
            if (!action) return;
            const { name, data } = action;
            switch (name) {
                case PREVIEW_ACTIONS.reloadPageData:
                    this.page = preparePage(data);
                    await this.adjustIframe();
                    break;
                case PREVIEW_ACTIONS.scrollToSection:
                    this.scrollInto(this.$refs.pageBody?.$el, data.webId);
                    break;
                default:
                    logger.warn("Unknown action from parent:", action);
            }
        },
        async adjustIframe() {
            this.currentFont = setFontFamily(this.page.fontFamily, this.currentFont);
            await this.$nextTick();
            this.updateIframe();
            openGallery();
            searchQuery();
            this.waitForMedia();
        },
        updateIframe() {
            resizeAllGridItems();
            this.updateHeight();
        },
        updateHeight() {
            updateIframeHeight("page", this.page.uuid);
        },
        resizeIframe() {
            if (window.innerWidth !== this.windowWidth) {
                this.adjustIframe();
                this.windowWidth = window.innerWidth;
            }
        },
        waitForMedia() {
            const media = document.querySelectorAll("img, video");
            this.mediaLength = media.length;
            this.counter = 0;
            [].forEach.call(media, (e) => {
                e.complete ? this.incCounter() : e.addEventListener("load", this.incCounter, !1);
            });
        },
        incCounter() {
            ++this.counter === this.mediaLength && this.updateIframe();
        },
    },
};
</script>
